"use client"

import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as DialogPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

/**
 * Unified Math Editor — MathLive mounts its virtual keyboard (and variant
 * popover) as a portal on `document.body`, OUTSIDE the dialog subtree. Radix
 * treats a pointer/focus event there as "outside" and dismisses the dialog,
 * which is the confirmed root cause of the math modal closing mid-use.
 *
 * `isMathKeyboardEvent` returns true when a Radix dismissal event originated
 * inside MathLive's keyboard/popover portal, so the dialog can `preventDefault`
 * it and stay open. Handles both the synthetic Radix events (which carry the
 * underlying DOM event on `detail.originalEvent`) and plain DOM events.
 */
const ML_KEYBOARD_SELECTOR =
  '[class*="ML__"], .ML__keyboard, .ML__popover, [data-ml-keyboard]'

export function isMathKeyboardEvent(event: Event): boolean {
  const detail = (event as CustomEvent).detail as
    | { originalEvent?: Event }
    | undefined
  const target =
    (detail?.originalEvent?.target as Element | null) ??
    (event.target as Element | null)
  if (!target || typeof target.closest !== "function") return false
  return target.closest(ML_KEYBOARD_SELECTOR) != null
}

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  dismissIgnoresMathKeyboard = false,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  /**
   * Opt-in: keep the dialog open when a dismissal event (pointer-down /
   * focus / generic interact outside) originates inside MathLive's virtual
   * keyboard portal. Only the math-equation modal sets this — every other
   * dialog behaves identically. `DialogPrimitive.Content` already forwards
   * the three handlers via `{...props}`; this prop just wires the guard.
   */
  dismissIgnoresMathKeyboard?: boolean
}) {
  // Compose the math-keyboard guard with any caller-provided handlers so the
  // opt-in never clobbers an explicit handler.
  const guard = React.useCallback(
    (
      event: Event,
      passthrough?: (e: never) => void
    ) => {
      if (dismissIgnoresMathKeyboard && isMathKeyboardEvent(event)) {
        event.preventDefault()
        return
      }
      passthrough?.(event as never)
    },
    [dismissIgnoresMathKeyboard]
  )

  const {
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    ...restProps
  } = props

  const dismissHandlers = dismissIgnoresMathKeyboard
    ? {
        onPointerDownOutside: (e: Parameters<NonNullable<typeof onPointerDownOutside>>[0]) =>
          guard(e as unknown as Event, onPointerDownOutside),
        onFocusOutside: (e: Parameters<NonNullable<typeof onFocusOutside>>[0]) =>
          guard(e as unknown as Event, onFocusOutside),
        onInteractOutside: (e: Parameters<NonNullable<typeof onInteractOutside>>[0]) =>
          guard(e as unknown as Event, onInteractOutside),
      }
    : { onPointerDownOutside, onFocusOutside, onInteractOutside }

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="dialog-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 outline-none sm:max-w-lg",
          className
        )}
        {...restProps}
        {...dismissHandlers}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
